import React, { use, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Favorite, Home } from '@mui/icons-material';
import { useLogout } from '@/hooks/auth/useLogout';
import { useClickOutside } from '@/hooks/common/customHooks';
import NotificationBell from '../../notifications/NotificationBell';
import { NannieLogo, PicassoTitle } from '../../ui/BrandingComponents';
import { ErrorIcon } from '../../ui/IconComponents';
import ProfileMenu, { getProfileMenuSections } from '../../common/ProfileMenu';
import { DeviceContext } from '@/hooks/common/DeviceContext';

interface HeaderProps {
  isAdmin: boolean;
  userEmail: string | null;
  showNotifications?: boolean;
  showFavorites?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isAdmin, userEmail, showNotifications = true, showFavorites = true }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { logout, isLoggingOut, logoutError, clearLogoutError } = useLogout();
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setShowDropdown(false));
  const location = useLocation();
  const navigate = useNavigate();

  // Check if currently on favourites page
  const isFavouritesPage = location.pathname === '/favourites';

  // Handle video notification click - open video modal
  const handleVideoNotificationClick = (videoId: string) => {
    // Dispatch custom event to communicate with VideoGallery
    globalThis.dispatchEvent(new CustomEvent('openVideoModal', { 
      detail: { videoId } 
    }));
  };

  // Handle favorites click - toggle between favorites page and content creation page
  const handleFavoritesClick = () => {
    if (isFavouritesPage) {
      // If already on favourites page, go to content creation page
      navigate('/dashboard');
    } else {
      // Otherwise, go to favourites page
      navigate('/favourites');
    }
  };

  const handleLogout = async () => {
    setShowDropdown(false);
    await logout();
  };

  const handleRefresh = () => {
    globalThis.location.reload();
  };

  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };

  return (
    <>
      <header className="w-full px-4 sm:px-6 pt-4 pb-0 sm:py-4 bg-white">
        <div className="flex items-center justify-between mx-auto">
          {isMobile ? <PicassoTitle /> : <NannieLogo onClick={handleRefresh} />}

          <div className="flex items-center">
            {/* Favorites/Home Icon */}
            {showFavorites && (
              <IconButton
                onClick={handleFavoritesClick}
                color="inherit"
                size="large"
                aria-label={isFavouritesPage ? "Go to dashboard" : "View favorites"}
                sx={{ marginRight: '-4px' }}
              >
                {isFavouritesPage ? (
                  <Home sx={{ color: 'black' }} />
                ) : (
                  <Favorite sx={{ color: 'black' }} />
                )}
              </IconButton>
            )}

            {/* Notification Bell */}
            {showNotifications && (
              <NotificationBell
                userEmail={userEmail}
                isAdmin={isAdmin}
                onVideoClick={handleVideoNotificationClick}
              />
            )}

            {/* Profile Image */}
            <div className="relative" ref={dropdownRef}>
              <button 
                type="button"
                id="profile-menu-button"
                className="w-[32px] h-[32px] rounded-full bg-white p-0"
                onClick={() => setShowDropdown(!showDropdown)}
                aria-label="Open profile menu"
                aria-expanded={showDropdown}
                aria-haspopup="menu"
              >
                <img 
                  src="https://storage.googleapis.com/nannie_profile_images/Profile_dog.webp" 
                  alt="Profile" 
                  className="w-full h-full object-cover rounded-full"
                />
              </button>
              <ProfileMenu
                showDropdown={showDropdown}
                sections={getProfileMenuSections(isAdmin, location.pathname)}
                onLogout={handleLogout}
                isLoggingOut={isLoggingOut}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Logout Error Modal */}
      {logoutError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <ErrorIcon className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-red-800 mb-2">Logout Failed</h3>
              <p className="text-red-600 mb-6">{logoutError}</p>
              <button
                onClick={clearLogoutError}
                className="w-full py-2 px-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;

